import { useEffect, useState } from "react";
import { Check, Target, Loader2, Pencil, Trash2 } from "lucide-react";
import api from "../lib/api";
import { GOAL_STATUS_OPTIONS_DRAWN, normalizeGoalStatus } from "../lib/goalStatus";
import { FailureCard } from "./GoalNotice";
import EditGoalSheet from "./EditGoalSheet";

/**
 * Update, Edit and Delete, in the opened goal (D282, D405: Edit opens `EditGoalSheet`). Anatomy and values are
 * `CoachGoals.dc.html` / `CoachPlayerGoals.dc.html`'s goal card: a read-only
 * Progress row and an Update control with a target icon; a 44px trash icon;
 * Update opens a tinted panel (Progress bar, Status, then Cancel as an
 * underlined link and a navy Save with a tick); Delete asks first, in the
 * page. The writes are the old card's: `PUT /goals/:id` with
 * `progress_percentage` and `status`, and `DELETE /goals/:id`. Failures show
 * in the page as the board's failure card, never as a toast (D57).
 *
 * Moving the slider derives the status as before (100 completes, above 0 is
 * in progress, 0 is not started); choosing Completed sets 100.
 */

const BOX_BTN = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 44,
  padding: "0 14px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--nw-navy)",
  cursor: "pointer",
};

const ROW_LABEL = { width: 64, flexShrink: 0, fontSize: 12, fontWeight: 600, color: "var(--nw-ink-lede)" };

const deriveStatus = (v) => (v >= 100 ? "completed" : v > 0 ? "in_progress" : "not_started");

export default function PlanGoalManage({ goal, onChanged, onDeleted }) {
  const id = goal.goal_id;
  const title = goal.target || goal.goal_title;
  const savedProgress = Number(goal.progress_percentage) || 0;
  const savedStatus = normalizeGoalStatus(goal.status);

  const [editing, setEditing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Bumped on each Edit click; the sheet is keyed by it so every opening starts from the goal as it is now.
  const [editKey, setEditKey] = useState(0);
  const [progress, setProgress] = useState(savedProgress);
  const [status, setStatus] = useState(savedStatus);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  // A different goal, or fresh server values after a save: start from them.
  useEffect(() => {
    setProgress(savedProgress);
    setStatus(savedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, savedProgress, savedStatus]);

  const cancel = () => {
    setEditing(false);
    setSaveError(false);
    setProgress(savedProgress);
    setStatus(savedStatus);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      await api.put(`/goals/${id}`, { progress_percentage: progress, status });
      setEditing(false);
      await onChanged?.();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  const remove = async () => {
    setDeleting(true);
    setDeleteError(false);
    try {
      await api.delete(`/goals/${id}`);
      await onDeleted?.();
    } catch {
      setDeleteError(true);
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`goal-manage-${id}`}
      className="flex flex-col"
      style={{ gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      {!editing && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>Progress</span>
            <span data-testid={`goal-progress-${id}`} style={{ fontSize: 13, fontWeight: 700, color: "var(--nw-navy)" }}>
              {savedProgress}%
            </span>
          </div>
          <div aria-hidden="true" style={{ height: 6, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-line)" }}>
            <div style={{ width: `${savedProgress}%`, height: 6, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-navy)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button type="button" style={BOX_BTN} onClick={() => setEditing(true)} data-testid={`goal-update-${id}`}>
                <Target size={15} strokeWidth={2} aria-hidden="true" />
                Update
              </button>
              <button type="button" style={BOX_BTN} onClick={() => { setEditKey((k) => k + 1); setEditOpen(true); }} data-testid="goal-edit-btn">
                <Pencil size={15} strokeWidth={2} aria-hidden="true" />
                Edit
              </button>
            </div>
            {!confirming && (
              <button
                type="button"
                aria-label="Delete goal"
                onClick={() => setConfirming(true)}
                data-testid={`goal-del-${id}`}
                style={{ width: 44, height: 44, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--nw-radius-control)", border: "1px solid var(--nw-line)", background: "var(--nw-surface)", cursor: "pointer" }}
              >
                <Trash2 size={16} color="var(--nw-alert)" strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div
          className="flex flex-col"
          data-testid={`goal-update-form-${id}`}
          style={{ gap: 10, padding: 12, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label htmlFor={`goal-slider-${id}`} style={ROW_LABEL}>
              Progress
            </label>
            <input
              id={`goal-slider-${id}`}
              type="range"
              min="0"
              max="100"
              value={progress}
              disabled={saving}
              onChange={(e) => {
                const v = Number(e.target.value);
                setProgress(v);
                setStatus(deriveStatus(v));
              }}
              data-testid={`goal-slider-${id}`}
              className="nw-range"
              style={{ flexGrow: 1, minWidth: 0, "--nw-fill": `${progress}%` }}
            />
            <span style={{ width: 40, textAlign: "right", fontSize: 14, fontWeight: 700, color: "var(--nw-navy)" }}>{progress}%</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label htmlFor={`goal-status-${id}`} style={ROW_LABEL}>
              Status
            </label>
            <select
              id={`goal-status-${id}`}
              value={status}
              disabled={saving}
              onChange={(e) => {
                const s = e.target.value;
                setStatus(s);
                if (s === "completed") setProgress(100);
              }}
              data-testid={`goal-status-${id}`}
              style={{ flexGrow: 1, minWidth: 0, minHeight: 44, padding: "0 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", fontSize: 15, color: "var(--nw-ink)", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
            >
              {GOAL_STATUS_OPTIONS_DRAWN.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {saveError && <FailureCard data-testid={`goal-update-error-${id}`} title="Failed to update progress" />}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16 }}>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              data-testid={`goal-update-cancel-${id}`}
              style={{ display: "inline-flex", alignItems: "center", minHeight: 44, background: "none", border: "none", padding: 0, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`goal-update-save-${id}`}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 44, padding: "0 16px", background: "var(--nw-navy)", color: "#FFFFFF", border: "none", fontWeight: 600, fontSize: 14, borderRadius: "var(--nw-radius-control)", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Check size={16} strokeWidth={2} aria-hidden="true" />}
              Save
            </button>
          </div>
        </div>
      )}

      <EditGoalSheet key={editKey} goal={goal} open={editOpen} onOpenChange={setEditOpen} onSaved={onChanged} />

      {confirming && (
        <div role="group" aria-label="Delete this goal" data-testid={`goal-del-confirm-${id}`} className="flex flex-col" style={{ gap: 10 }}>
          <div style={{ fontSize: 15, lineHeight: 1.5, fontWeight: 700, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>Delete "{title}"?</div>
          {deleteError && (
            <FailureCard data-testid={`goal-del-error-${id}`} body="Nothing has been lost — we just couldn't reach the server." />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              disabled={deleting}
              onClick={() => {
                setConfirming(false);
                setDeleteError(false);
              }}
              data-testid={`goal-del-keep-${id}`}
              style={{ ...BOX_BTN, minHeight: 48, fontSize: 14 }}
            >
              Keep it
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={remove}
              data-testid={`goal-del-yes-${id}`}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 48, padding: "0 16px", background: "var(--nw-alert)", color: "#FFFFFF", border: "none", fontWeight: 600, fontSize: 15, borderRadius: "var(--nw-radius-control)", cursor: deleting ? "not-allowed" : "pointer" }}
            >
              {deleting ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
              Delete goal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
